import type { Furniture, Vec2 } from "../types";

/**
 * Pure math behind the on-canvas furniture handles. Kept renderer-free so it
 * can be unit tested without Konva.
 */

export type ResizeEdge = "left" | "right" | "front" | "back";

export const MIN_FURNITURE_SIZE_IN = 6;
export const MAX_FURNITURE_SIZE_IN = 240;

/**
 * Rotation (degrees, clockwise, 0 = front faces +y) that points the piece's
 * *back* edge toward `pointer`, given the piece center — the rotation grip
 * lives above the back edge. `shift` snaps to 15°; otherwise angles within
 * 3° of a cardinal snap softly to it.
 */
export function rotationFromPointer(center: Vec2, pointer: Vec2, shift: boolean): number {
  const dx = pointer.x - center.x;
  const dy = pointer.y - center.y;
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
  deg = ((deg % 360) + 360) % 360;
  if (shift) {
    deg = Math.round(deg / 15) * 15;
  } else {
    for (const c of [0, 90, 180, 270, 360]) {
      if (Math.abs(deg - c) < 3) deg = c;
    }
  }
  deg = Math.round(deg * 10) / 10;
  return deg % 360;
}

export function snapSizeIn(v: number, snapIn: number): number {
  const step = snapIn > 0 ? snapIn : 1;
  const snapped = Math.round(v / step) * step;
  return Math.min(MAX_FURNITURE_SIZE_IN, Math.max(MIN_FURNITURE_SIZE_IN, snapped));
}

export interface ResizeResult {
  patch: Pick<Furniture, "x" | "y"> & Partial<Pick<Furniture, "width" | "depth">>;
  /** New half extent along the dragged axis, inches. */
  halfIn: number;
}

/**
 * Resize by dragging one edge while the opposite edge stays pinned.
 * `localPointerIn` is the pointer in the piece's local frame (inches, origin
 * at center, unrotated). Returns the new size and the shifted center.
 */
export function resizeFromEdge(
  f: Pick<Furniture, "x" | "y" | "width" | "depth" | "rotation">,
  edge: ResizeEdge,
  localPointerIn: Vec2,
  snapIn: number
): ResizeResult {
  const rad = (f.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  if (edge === "left" || edge === "right") {
    const sign = edge === "right" ? 1 : -1;
    const halfW = f.width / 2;
    // Distance from the pinned (opposite) edge to the pointer.
    const newW = snapSizeIn(sign * localPointerIn.x + halfW, snapIn);
    const newHalf = newW / 2;
    const oppositeEdge = -sign * halfW;
    const newCenterLocalX = oppositeEdge + sign * newHalf;
    return {
      patch: {
        width: newW,
        x: f.x + newCenterLocalX * cos,
        y: f.y + newCenterLocalX * sin,
      },
      halfIn: newHalf,
    };
  }
  const sign = edge === "front" ? 1 : -1;
  const halfD = f.depth / 2;
  const newD = snapSizeIn(sign * localPointerIn.y + halfD, snapIn);
  const newHalf = newD / 2;
  const oppositeEdge = -sign * halfD;
  const newCenterLocalY = oppositeEdge + sign * newHalf;
  return {
    patch: {
      depth: newD,
      x: f.x - newCenterLocalY * sin,
      y: f.y + newCenterLocalY * cos,
    },
    halfIn: newHalf,
  };
}

/**
 * Grab-offset resize: `deltaIn` is how far the finger has moved along the
 * edge's outward normal since the drag began, so the edge moves by exactly
 * that much and never jumps to the finger. Under half a snap step the size
 * stays put, so an off-grid piece doesn't snap the moment it's touched.
 */
export function resizeFromEdgeDelta(
  start: Pick<Furniture, "x" | "y" | "width" | "depth" | "rotation">,
  edge: ResizeEdge,
  deltaIn: number,
  snapIn: number
): ResizeResult {
  const horizontal = edge === "left" || edge === "right";
  const half = (horizontal ? start.width : start.depth) / 2;
  const step = snapIn > 0 ? snapIn : 1;
  if (Math.abs(deltaIn) < step / 2) {
    return {
      patch: {
        x: start.x,
        y: start.y,
        ...(horizontal ? { width: start.width } : { depth: start.depth }),
      },
      halfIn: half,
    };
  }
  const sign = edge === "right" || edge === "front" ? 1 : -1;
  const reach = sign * (half + deltaIn);
  return resizeFromEdge(start, edge, horizontal ? { x: reach, y: 0 } : { x: 0, y: reach }, snapIn);
}

/** Distance one nudge moves a piece, inches: one grid step, or 1" with snapping off. */
export function nudgeStep(snapIn: number): number {
  return snapIn > 0 ? snapIn : 1;
}

/**
 * Screen-pixel handle sizes. The visible grips stay small. On touch the
 * pills sit fully outside the outline and their pads only grow outward, so a
 * finger anywhere on the body moves the piece.
 */
export const HANDLE_METRICS = {
  mouse: { rotateOffset: 30, gripR: 7, rotateHit: 12, edgeW: 22, edgeH: 8, edgeHit: 10 },
  touch: {
    rotateOffset: 40,
    gripR: 11,
    rotateHit: 22,
    edgeW: 32,
    edgeH: 12,
    /** Space between the outline and the pill. */
    edgeGap: 2,
    /**
     * Outward from the silhouette. More than this covers the next piece at
     * phone fit-zoom (the sofa's front edge sits ~23px from the coffee table).
     */
    edgeOut: 22,
    /** Inward from the silhouette. Any reach into the body steals moves. */
    edgeIn: 0,
    /** Along the edge, centered on the grip, at most. */
    edgeAlong: 64,
  },
} as const;

export interface PxRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A touch edge grip in the piece's local frame (screen px, origin at the
 * center, unrotated): the visible pill just outside the outline and the pad
 * that catches the finger, which starts at the outline and runs outward.
 * Along the edge the pad is a third of the edge, between the pill's width
 * and `edgeAlong`, so neighbouring pads don't meet at the corners.
 */
export function touchEdgeGrip(
  edge: ResizeEdge,
  halfW: number,
  halfD: number
): { pill: PxRect; hit: PxRect } {
  const m = HANDLE_METRICS.touch;
  const horizontal = edge === "front" || edge === "back";
  const edgeLen = horizontal ? halfW * 2 : halfD * 2;
  const along = Math.min(m.edgeAlong, Math.max(m.edgeW, edgeLen / 3));
  const offset = m.edgeGap + m.edgeH;
  if (horizontal) {
    const sign = edge === "front" ? 1 : -1;
    const near = sign * halfD;
    return {
      pill: {
        x: -m.edgeW / 2,
        y: sign > 0 ? near + m.edgeGap : near - offset,
        width: m.edgeW,
        height: m.edgeH,
      },
      hit: {
        x: -along / 2,
        y: sign > 0 ? near - m.edgeIn : near - m.edgeOut,
        width: along,
        height: m.edgeOut + m.edgeIn,
      },
    };
  }
  const sign = edge === "right" ? 1 : -1;
  const near = sign * halfW;
  return {
    pill: {
      x: sign > 0 ? near + m.edgeGap : near - offset,
      y: -m.edgeW / 2,
      width: m.edgeH,
      height: m.edgeW,
    },
    hit: {
      x: sign > 0 ? near - m.edgeIn : near - m.edgeOut,
      y: -along / 2,
      width: m.edgeOut + m.edgeIn,
      height: along,
    },
  };
}

/** Touch rotation knob hit disk in the piece's local frame (screen px). */
export function touchRotateHit(halfD: number): { x: number; y: number; r: number } {
  const m = HANDLE_METRICS.touch;
  return { x: 0, y: -halfD - m.rotateOffset, r: m.gripR + m.rotateHit / 2 };
}

/**
 * When a selected piece is big enough on a touch screen to carry direct
 * grips without them crowding the body. Below this, a selected piece only
 * moves, and Adjust mode resizes it.
 */
export const TOUCH_GRIPS = {
  /** Shorter side, screen px, to show the grips. */
  showShort: 52,
  /** Shorter side to hide them again once shown, so a pinch doesn't flicker them. */
  hideShort: 44,
  /** Longer side, screen px. */
  showLong: 96,
  hideLong: 88,
} as const;

export type TouchHandle = ResizeEdge | "rotate";

export interface TouchHandleLayout {
  /** The piece is big enough for direct grips (hysteresis applied). */
  show: boolean;
  edges: ResizeEdge[];
  rotate: boolean;
}

/**
 * Which direct grips a selected piece shows on touch. `wasShowing` is the
 * previous answer (hysteresis); `neighborsHit` marks handles whose pad would
 * land on another piece, which are left out so a tap there selects it.
 */
export function touchHandleLayout(
  screenW: number,
  screenH: number,
  wasShowing: boolean,
  neighborsHit: Partial<Record<TouchHandle, boolean>> = {}
): TouchHandleLayout {
  const short = Math.min(screenW, screenH);
  const long = Math.max(screenW, screenH);
  const show = wasShowing
    ? short >= TOUCH_GRIPS.hideShort && long >= TOUCH_GRIPS.hideLong
    : short >= TOUCH_GRIPS.showShort && long >= TOUCH_GRIPS.showLong;
  if (!show) return { show: false, edges: [], rotate: false };
  const all: ResizeEdge[] = ["left", "right", "front", "back"];
  return {
    show: true,
    edges: all.filter((e) => !neighborsHit[e]),
    rotate: !neighborsHit.rotate,
  };
}

/** Adjust-mode knobs, screen px. */
export const ADJUST_RING = {
  /** Finger target diameter. */
  knobHit: 44,
  /** Visible knob radius (a ~24px dot). */
  knobR: 12,
  /** Minimum space between two knob targets. */
  gap: 8,
  /** Knob center past the outline: the target clears the body. */
  edgeOffset: 26,
  /** The ring is at least 112 × 112 around the center. */
  minHalf: 56,
} as const;

export interface AdjustRingLayout {
  /** Half extents of the knob ring. */
  halfX: number;
  halfY: number;
  knobs: Record<TouchHandle, Vec2>;
  /** Line from each edge's midpoint out to its knob. */
  guides: Record<TouchHandle, [Vec2, Vec2]>;
}

/**
 * Knob positions for Adjust mode in the piece's local frame (screen px). The
 * ring never shrinks below `minHalf`, so on a 16px armchair the four edge
 * knobs and the rotation knob still never overlap. The rotation knob sits
 * one target plus a gap above the back knob.
 */
export function adjustRingLayout(screenW: number, screenH: number): AdjustRingLayout {
  const r = ADJUST_RING;
  const w2 = Math.max(0, screenW) / 2;
  const h2 = Math.max(0, screenH) / 2;
  const halfX = Math.max(w2 + r.edgeOffset, r.minHalf);
  const halfY = Math.max(h2 + r.edgeOffset, r.minHalf);
  const rotateY = -halfY - (r.knobHit + r.gap);
  const knobs: Record<TouchHandle, Vec2> = {
    left: { x: -halfX, y: 0 },
    right: { x: halfX, y: 0 },
    back: { x: 0, y: -halfY },
    front: { x: 0, y: halfY },
    rotate: { x: 0, y: rotateY },
  };
  return {
    halfX,
    halfY,
    knobs,
    guides: {
      left: [{ x: -w2, y: 0 }, knobs.left],
      right: [{ x: w2, y: 0 }, knobs.right],
      back: [{ x: 0, y: -h2 }, knobs.back],
      front: [{ x: 0, y: h2 }, knobs.front],
      rotate: [knobs.back, knobs.rotate],
    },
  };
}
